import React, { useState } from 'react';
import { Box, Typography, ToggleButtonGroup, ToggleButton, Paper, Divider } from '@mui/material';
import { oldFormatExample, newFormatExample } from '../utils/testAnalysisFormat';
import AnalysisResults from './module1/AnalysisResults';

/**
 * Test component to demonstrate both the old and new analysis results formats.
 * This is purely for development and testing purposes.
 */
const TestAnalysisResults: React.FC = () => {
  const [format, setFormat] = useState<'old' | 'new'>('new');

  const handleFormatChange = (
    event: React.MouseEvent<HTMLElement>,
    newFormat: 'old' | 'new' | null,
  ) => {
    if (newFormat !== null) {
      setFormat(newFormat);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Analysis Results Format Test
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          This page demonstrates how the AnalysisResults component renders both the old (object-based) and new (array-based with keywords) formats.
          Toggle between formats to see the differences in presentation.
        </Typography>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Select Format:
          </Typography>
          <ToggleButtonGroup
            value={format}
            exclusive
            onChange={handleFormatChange}
            aria-label="format selector"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="old" aria-label="old format">
              Old Format (Object-Based)
            </ToggleButton>
            <ToggleButton value="new" aria-label="new format">
              New Format (Array-Based with Keywords)
            </ToggleButton>
          </ToggleButtonGroup>
          
          <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
            <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
              {format === 'old' 
                ? JSON.stringify(oldFormatExample, null, 2).substring(0, 500) + '...'
                : JSON.stringify(newFormatExample, null, 2).substring(0, 500) + '...'}
            </Typography>
          </Paper>
        </Box>
      </Paper>
      
      <Typography variant="h6" gutterBottom>
        Rendered Component:
      </Typography>
      <Paper sx={{ p: 0 }}>
        <AnalysisResults
          data={format === 'old' ? oldFormatExample : newFormatExample}
          workflowId="test-workflow-123"
          isProcessing={false}
          serviceAvailable={true}
          onReprocess={() => console.log('Reprocess clicked')}
          onResultsLoaded={(results) => console.log('Results loaded:', results)}
        />
      </Paper>
      
      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Key Differences:
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Old Format:</strong> Topics are in an object structure with topic names as keys. Each subtopic is also an object with subtopic names as keys pointing directly to stem word arrays.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>New Format:</strong> Topics are in an array structure with explicit "name" properties. Each subtopic is an object with "name", "stem_words", and "keywords" arrays.
        </Typography>
        <Typography variant="body2">
          The new format includes the actual keywords that match each subtopic, providing more context and examples for each category.
        </Typography>
      </Box>
    </Box>
  );
};

export default TestAnalysisResults;
